import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  TomTableActionsProps,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { appStore, useTablePreferencesConfig } from '@tom-ui/utils'
import { AllocationRuleDto } from '@yard/app/api'
import { useYardStores } from '@yard/AppProvider'
import {
  mapAllocationRuleDestinationToYardPositionDescriptor,
  mapAllocationRuleFacetsToString,
} from '@yard/components/AllocationRules/helpers/allocation-rule.mapper'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAllocationRuleTemplateMessageHandler } from '../../hooks/useAllocationRuleTemplateMessageHandler'

interface Props {
  store: AllocationRuleStore
}

export const AllocationRulesTemplateTable = observer(({ store }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const [deleteItems, setDeleteItems] = useState<{ id: string; name: string }[]>([])
  const { getDeleteSuccessMessage, getExceptionMessage } = useAllocationRuleTemplateMessageHandler()
  const { yardBlockStore } = useYardStores()

  const handleDelete = async () => {
    if (!deleteItems.length) return
    try {
      const requests = deleteItems.map(item => store.deleteTemplateAllocationRule(item.id))

      await Promise.all(requests)
      appStore.setShowAlert('success', getDeleteSuccessMessage())
      setDeleteItems([])
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<AllocationRuleDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'facets',
        header: t('fields', 'Fields'),
        accessorFn: row => mapAllocationRuleFacetsToString(row.facets, t, row.enforcementMode),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'destinations',
        header: t('destinations', 'Destinations'),
        accessorFn: row => {
          const destinations = row.destinations.map(dest => {
            const block = yardBlockStore.yardBlocks[dest.blockId]
            return mapAllocationRuleDestinationToYardPositionDescriptor(
              block,
              dest.minBay,
              dest.maxBay,
              dest.minRow,
              dest.maxRow,
            )
          })

          return destinations.join(', ')
        },
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t, yardBlockStore.yardBlocks],
  )

  const tableActions = useMemo<TomTableActionsProps<AllocationRuleDto>[]>(
    () => [
      {
        label: t('delete', 'Delete'),
        onClick: rows => {
          const itemsToDelete = rows.map(row => ({
            id: row.original.id,
            name: row.original.name,
          }))
          setDeleteItems(itemsToDelete)
        },
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'yard/allocation-rules-template',
  )

  const table = useTomTable<AllocationRuleDto>(
    {
      columns,
      data: store.templateRules,
      enableExpanding: false,
      layoutMode: 'grid',
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => navigate(`${row.original.id}`),
            testId: `edit-${row.original.id}`,
          },
          {
            label: t('delete', 'Delete'),
            onClick: () => setDeleteItems([{ id: row.original.id, name: row.original.name }]),
            testId: `delete-${row.original.id}`,
          },
        ]
        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='allocation-rule-template-options-btn'
          />
        )
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 202px)',
          minHeight: 'calc(100vh - 202px)',
        },
      },
    },
    tableActions,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteItems.length > 0}
        title={
          deleteItems.length === 1
            ? t('deleteAllocationRuleTemplate', 'Delete allocation rule template?')
            : t('deleteAllocationRuleTemplates', 'Delete allocation rule templates?')
        }
        message={
          deleteItems.length === 1
            ? t(
                'deleteAllocationRuleTemplateMessage',
                'Allocation rule template {rule} will be deleted.',
                { rule: deleteItems[0].name },
              )
            : t(
                'deleteAllocationRuleTemplatesMessage',
                'Allocation rule templates {rules} will be deleted.',
                { rules: deleteItems.map(item => item.name).join(', ') },
              )
        }
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteItems([])}
      />
    </>
  )
})
